<script setup>

import {ErrorMessage, Field, Form} from 'vee-validate'
import {computed, reactive, ref, watch} from 'vue'
import axios from 'axios'
import {useAddressField} from '../../composables/useAddressField'
import GoogleAddress from './../Google/GoogleAddress.vue'
import {useFormSubmit} from '../../composables/useFormSubmit'

const { showServerSideValidateErrors, enquirySubmitDataLayer, sendFBLeadData } = useFormSubmit()
const {showAddressFields, addressValueChange, showCustomAddressLink, toggleCustomAddress} = useAddressField()

const props = defineProps({
  villageName: {
    type: String,
    default: '',
  },
  formTitle: {
    type: String,
    default: 'Request an information pack',
  },
  showFormTypes : {
    type: Boolean,
    default: false,
  },
  formTypes : {
    type: Array,
    default: () => ['Information pack', 'Apartment pricing'],
  },
})

const validationSchema = computed(() => {
  let schema = {
    first_name: 'required',
    last_name: 'required',
    email: 'required|email',
    phone_number: 'required|phoneNumber',
  }

  if (showAddressFields.value) {
    schema.street_address = 'required'
    schema.postcode = 'required|numeric'
    schema.city = 'required'
  }

  if (props.showFormTypes) {
    schema.subject = 'required'
  }

  return schema
})

const confirmation = reactive({show: false, message: '', class: ''})
let isSubmitting = ref(false)
const showEnquirySuccess = ref(false)
const successTitle = ref('Request an information pack')
const successMessage = ref('Your request for an information pack was sent successfully!')
const subject = ref('Request an information pack');
const showAddress = ref(true);
let messagePlaceHolder = `Please send me an information pack for ${props.villageName}`

const getSubjectValue = (type) => {
  if (type === 'Villa pricing') {
    return 'Request villa pricing';
  }

  if (type === 'Apartment pricing') {
    return 'Request apartment pricing';
  }

  return `Request an ${type.charAt(0).toLowerCase()}${type.slice(1)}`;
};

if (props.showFormTypes) {
  watch(subject, (newValue) => {
    successTitle.value = newValue;
    if (newValue === 'Request an information pack') {
      messagePlaceHolder = `Please send me an information pack for ${props.villageName}.`;
      showAddress.value = true;
    } else if (newValue === 'Request apartment pricing') {
      messagePlaceHolder = `Please send me apartment pricing for ${props.villageName}.`;
      showAddress.value = false;
      successMessage.value = 'Your request for apartment pricing was sent successfully!';
    } else if (newValue === 'Request villa pricing') {
      messagePlaceHolder = `Please send me villa pricing for ${props.villageName}.`;
      showAddress.value = false;
      successMessage.value = 'Your request for villa pricing was sent successfully!';
    }
  }, { immediate: true });
}


const onSubmit = async () => {
  isSubmitting.value = true
  confirmation.show = true
  confirmation.class = 'message--warning'
  confirmation.message = 'Submitting....'
  const form  = document.getElementById('villageInfoPackEnquiryForm');
  let formData = new FormData(form);

  if (document.getElementById('message') && !formData.get('message')) {
    formData.set('message', messagePlaceHolder);
  }

  window.grecaptcha.execute(document.getElementById('mySiteKeyVariable').value, {
    action: formData.get('captcha_action'),
  }).then((token) => {
    const submitButton = form.querySelector('.submit');
    submitButton.disabled = true;
    formData.set('g-recaptcha-response', token);
    axios.post('/enquiry-submitted', formData).then(function () {
      enquirySubmitDataLayer(form);
      sendFBLeadData(formData.get('subject'));
      // Reset the form
      form.reset();
      showEnquirySuccess.value = true;
    }).catch(error => {
      showServerSideValidateErrors(error, isSubmitting, confirmation)
    });
  });
}
</script>

<template>
  <Form
    :validation-schema="validationSchema"
    v-slot="{ errors }"
    @submit="onSubmit"
    id="villageInfoPackEnquiryForm"
    v-if="!showEnquirySuccess"
  >
    <slot name="token"></slot>
    <p class="fs-18 p-0 m-0 text-left font-weight-bold mb-2" v-html="formTitle"></p>
    <div v-show="showFormTypes">
      <div
        class="radio-custom radio-custom--white"
        :class="{'mb-4': index === formTypes.length - 1}"
        v-for="(type, index) in formTypes"
        :key="type"
      >
        <Field
          type="radio"
          :id="type.replace(/\s+/g, '_').toLowerCase()"
          name="subject"
          :value="getSubjectValue(type)"
          v-model="subject"
        />
        <label class="form-check-label fs-13 mb-1" :for="type.replace(/\s+/g, '_').toLowerCase()">{{ type }}</label>
      </div>
    </div>
    <div class="form-group form-group--input mb-1">
      <label for="name" class="sr-only">First name</label>
      <Field
        id="first_name"
        name="first_name"
        placeholder="First name *"
        class="form-control form-control--bg-white"
        :class="{'is-danger': errors['first_name'] }"
      />
      <ErrorMessage name="first_name" class="is-danger" />
    </div>
    <div class="form-group form-group--input mb-1">
      <label for="last_name" class="sr-only">Last name</label>
      <Field
        name="last_name"
        placeholder="Last name *"
        class="form-control form-control--bg-white"
        :class="{'is-danger': errors['last_name'] }"
        tabindex="2"
        id="last_name"
      />
      <ErrorMessage name="last_name" class="is-danger" />
    </div>
    <div class="form-group form-group--input mb-1">
      <label for="email" class="sr-only">Email</label>
      <Field
        tabindex="3"
        id="email"
        name="email"
        placeholder="Email *"
        class="form-control form-control--bg-white"
        :class="{'is-danger': errors['email'] }"
      />
      <ErrorMessage name="email" class="is-danger" />
    </div>
    <div class="form-group form-group--input mb-1">
      <label for="phone_number" class="sr-only">Phone</label>
      <Field
        tabindex="4"
        id="phone_number"
        name="phone_number"
        placeholder="Phone *"
        class="form-control form-control--bg-white"
        :class="{'is-danger': errors['phone_number'] }"
      />
      <ErrorMessage name="phone_number" class="is-danger" />
    </div>
    <div v-if="showAddressFields">
      <div class="form-group form-group--input mb-1">
        <label for="street_address" class="sr-only">Street address</label>
        <Field
          tabindex="5"
          id="street_address"
          name="street_address"
          placeholder="Street Address *"
          class="form-control form-control--bg-white"
          :class="{'is-danger': errors['street_address'] }"
        />
        <ErrorMessage name="street_address" class="is-danger" />
      </div>

      <div class="form-group form-group--input">
        <label for="city" class="sr-only">City</label>
        <Field
          tabindex="6"
          id="city"
          name="city"
          placeholder="City *"
          class="form-control form-control--bg-white"
          :class="{'is-danger': errors['city'] }"
        />
        <ErrorMessage name="city" class="is-danger" />
      </div>
      <div class="form-group form-group--input">
        <label for="postcode" class="sr-only">Post code</label>
        <Field
          tabindex="7"
          id="postcode"
          name="postcode"
          placeholder="Post code *"
          class="form-control form-control--bg-white"
          :class="{'is-danger': errors['postcode'] }"
        />
        <ErrorMessage name="postcode" class="is-danger" />
      </div>
      <div class="form-group form-group--input">
        <label for="country" class="sr-only">Country</label>
        <slot name="country"></slot>
      </div>
    </div>
    <div v-if="showAddress">
      <div class="form-group form-group--input" v-if="!showAddressFields">
        <label for="address" class="sr-only">Address</label>
        <google-address
          :address-id="'village-info-pack-enquiry'"
          v-on:addressvaluechange="addressValueChange"
          :classname="'form-control form-control--bg-white ph-16'"
        ></google-address>
        <div v-if="showCustomAddressLink" class="mt-2 fs-14 font-weight-600" >
          Can't find your address? <a href="#" @click.prevent="toggleCustomAddress" id="enterCustomAddress">Enter it here</a>
        </div>
      </div>
    </div>
    <div class="form-group form-group--input">
      <label for="message" class="sr-only">Message</label>
      <Field
        as="textarea"
        tabindex="9"
        id="message"
        rows="2"
        :placeholder="messagePlaceHolder"
        class="form-control form-control--textarea form-control--bg-white p-3 ph-15"
        name="message"
      ></Field>
    </div>
    <input type="hidden" name="captcha_action" value="village_enquiry">
    <div tabindex="11" class="checkbox-custom mt-2 mb-0">
      <input  type="checkbox" class="subscribe" id="subscribe_enquiry" name="subscribe" value="1" checked>
      <label class="form-check-label fs-12 mb-0" for="subscribe_enquiry">
        I would like to receive emails with village info, updates and more from Village Guide
      </label>
    </div>

    <transition name="fade">
      <div :class="confirmation.class" class="message" v-if="confirmation.show">{{ confirmation.message }}</div>
    </transition>
    <div class="form-group mt-2 mb-0">
      <slot name="hiddenfields"></slot>
      <button tabindex="12" type="submit" class="button button--green button--h-45 width-full fs-14 submit">
        Send
      </button>
    </div>
  </Form>
  <div v-if="showEnquirySuccess" v-cloak class="success-message">
    <p class="fs-18 p-0 m-0 text-center font-weight-bold mb-5" v-html="successTitle"></p>
    <p class="p-0 m-0 text-center mb-6">
      <i class="icon icon-tick-circle "></i>
    </p>
    <p class="p-0 mt-0 fs-15 text-center" v-html="successMessage"></p>
    <p class="p-0 mt-0 fs-15 text-center mb-6">
      You will receive an email confirmation.
    </p>
  </div>
</template>
